import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle, Mail, ArrowRight, Download, ExternalLink } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OrderConfirmation = () => {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const response = await axios.get(`${API}/orders/${orderId}`);
        setOrder(response.data);
      } catch (err) {
        setError("Order not found");
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [orderId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-pulse text-center">
          <div className="w-16 h-16 skeleton rounded-full mx-auto mb-4" />
          <div className="h-6 w-48 skeleton rounded mx-auto mb-2" />
          <div className="h-4 w-32 skeleton rounded mx-auto" />
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-heading font-semibold text-xl mb-2">Order Not Found</h2>
          <p className="text-muted-foreground mb-4">We couldn't find that order.</p>
          <Link to="/courses">
            <Button>Browse Courses</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-12">
      <div className="container mx-auto px-4 lg:px-8 max-w-3xl">
        {/* Success Header */}
        <div className="text-center mb-12">
          <div className="w-20 h-20 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-accent" />
          </div>
          <h1 className="font-heading font-bold text-3xl lg:text-4xl text-primary mb-4" data-testid="confirmation-title">
            Payment Successful!
          </h1>
          <p className="text-lg text-muted-foreground">
            Thank you for your enrollment. Your order has been confirmed.
          </p>
        </div>

        {/* Order Details Card */}
        <Card className="mb-8" data-testid="order-details">
          <CardContent className="p-6 lg:p-8">
            <div className="flex items-center justify-between mb-6 pb-6 border-b">
              <div>
                <p className="text-sm text-muted-foreground">Order ID</p>
                <p className="font-mono text-sm">{order.id}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Status</p>
                <span className="inline-flex items-center px-2 py-1 rounded-full bg-accent/10 text-accent text-sm font-medium">
                  {order.status === "completed" ? "Completed" : order.status}
                </span>
              </div>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Course</p>
                  <p className="font-medium">{order.course_title}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Format</p>
                  <p className="font-medium capitalize">{order.delivery_format?.replace('-', ' ')}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">{order.participants > 1 ? "Primary Contact" : "Participant"}</p>
                  <p className="font-medium">{order.customer_name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-medium">{order.customer_email}</p>
                </div>
              </div>

              {order.company_name && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Company</p>
                    <p className="font-medium">{order.company_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Participants</p>
                    <p className="font-medium">{order.participants}</p>
                  </div>
                </div>
              )}

              {order.discount_percent > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-green-800">Corporate Discount Applied</span>
                    <span className="font-semibold text-green-800">{order.discount_percent}% off</span>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                <div>
                  <p className="text-sm text-muted-foreground">Amount Paid</p>
                  <p className="font-heading font-bold text-xl text-brand">${order.amount}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Payment Method</p>
                  <p className="font-medium">{order.payment_method === "stripe" ? "Credit/Debit Card" : "Bank Transfer"}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Next Steps */}
        <Card className="mb-8">
          <CardContent className="p-6 lg:p-8">
            <h2 className="font-heading font-semibold text-xl mb-4">What Happens Next?</h2>
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-brand/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-semibold text-brand">1</span>
                </div>
                <div>
                  <h3 className="font-medium">Confirmation Email</h3>
                  <p className="text-sm text-muted-foreground">
                    You'll receive a confirmation email at {order.customer_email} with your enrollment details.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-brand/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-semibold text-brand">2</span>
                </div>
                <div>
                  <h3 className="font-medium">Course Access</h3>
                  <p className="text-sm text-muted-foreground">
                    Our team will process your enrollment and send course access instructions within 24 hours.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-brand/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-semibold text-brand">3</span>
                </div>
                <div>
                  <h3 className="font-medium">Start Learning</h3>
                  <p className="text-sm text-muted-foreground">
                    Begin your certification journey with our expert-led training materials.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/courses">
            <Button variant="outline" className="w-full sm:w-auto">
              Browse More Courses
            </Button>
          </Link>
          <Link to="/">
            <Button className="w-full sm:w-auto bg-brand hover:bg-brand-dark text-white">
              Return to Home
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>

        {/* Support Note */}
        <p className="text-center text-sm text-muted-foreground mt-8">
          Have questions? Contact us at{" "}
          <a href="mailto:support@comply-cms.com" className="text-brand hover:underline">
            support@comply-cms.com
          </a>
        </p>
      </div>
    </div>
  );
};

export default OrderConfirmation;
